import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ExperienceTemplateStatus,
  IAutomationActionTemplateEnrollInExperience,
  IAutomationTemplate,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { sortBy } from 'lodash';
import { ArrowRight } from 'lucide-react';
import { SetStateAction, useMemo } from 'react';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../components/InfoBox';
import { Loader } from '../../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../../components/SomethingWentWrong';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import {
  getSelectableSupportingActors,
  getSupportingActorForDisplay
} from '../../../../../../../services/experiences/actorUsage';
import {
  getTplAdminTitle,
  useExperienceTemplateById,
  useExperienceTemplatesByOrgId
} from '../../../../../../../services/experiences/template';
import { useMappedLoadingValue } from '../../../../../../../services/firebase';
import { hasCurrentUserExperienceLevelScopes } from '../../../../../../../services/permissions';
import { toSearchTermRegexp } from '../../../../../../../services/user';
import { useActorDescriptorsFromTemplate } from '../../../../Actors';
import { MappingRows } from '../MappingRows';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem
} from '../../validation';

export const SupportingActorMappings = ({
  value,
  onChange,
  targetTpl,
  currentActors
}: {
  value: IAutomationActionTemplateEnrollInExperience['supportingActors'];
  onChange: (
    nextValue: IAutomationActionTemplateEnrollInExperience['supportingActors']
  ) => void;
  targetTpl: Doc<IExperienceTemplate>;
  currentActors: TaskBuilderContext['supportingActors'];
}) => {
  const { t } = useUnsafeTranslation();
  // A channel actor never resolves to a real user, so mapping to/from one
  // would always silently no-op.
  const currentOptions = useMemo(() => {
    return getSelectableSupportingActors(currentActors, {
      includeChannelActors: false
    });
  }, [currentActors]);
  const currentRemainingOptions = useMemo(() => {
    return currentOptions.filter((a) => !value.find((x) => x.from === a.key));
  }, [currentOptions, value]);

  const otherOptions = useMemo(() => {
    return getSelectableSupportingActors(targetTpl.data.supportingActors, {
      includeChannelActors: false
    });
  }, [targetTpl.data.supportingActors]);

  const otherRemainingOptions = useMemo(() => {
    return otherOptions.filter((a) => !value.find((x) => x.to === a.key));
  }, [otherOptions, value]);

  // A saved mapping can reference an actor that's since been archived (still
  // resolvable, just no longer a new-selection option) or actually deleted
  // (unresolvable). Either way, keep the current selection visible in the
  // dropdown — rather than letting it silently render blank — and report
  // which case it is so the caller can warn about it.
  const describeRowActor = (
    actors: TaskBuilderContext['supportingActors'],
    selectableOptions: typeof currentOptions,
    key: string
  ) => {
    if (!key || selectableOptions.some((o) => o.key === key)) {
      return { displayOptions: selectableOptions, isArchived: false, isMissing: false };
    }
    const currentSelection = getSupportingActorForDisplay(actors, key);
    return {
      displayOptions: currentSelection
        ? [...selectableOptions, currentSelection]
        : selectableOptions,
      isArchived: !!currentSelection,
      isMissing: !currentSelection
    };
  };

  return (
    <MappingRows
      rows={value}
      onChange={onChange}
      addLabel={t('Add Supporting Actor Mapping')}
      canAdd={!!currentRemainingOptions.length && !!otherRemainingOptions.length}
      createEmptyRow={() => ({
        from: currentRemainingOptions[0]?.key || '',
        to: otherRemainingOptions[0]?.key || '',
        type: 'fromCurrentTemplate' as const
      })}
      header={
        <FlexContainerVertical spacing={0.5}>
          <Typography variant="h6">{t('Supporting Actors')}</Typography>
          <div>
            {t(
              'Assign actors in the new experience based on actors in this experience.'
            )}
          </div>
        </FlexContainerVertical>
      }
      renderSource={(x, update) => {
        const { displayOptions, isArchived, isMissing } = describeRowActor(
          currentActors,
          currentOptions,
          x.from
        );
        return (
          <FormControl variant="outlined" fullWidth error={isArchived || isMissing}>
            <InputLabel>{t('Actor in this experience')}</InputLabel>
            <Select
              fullWidth
              label={t('Actor in this experience')}
              value={x.from}
              onChange={(ev) =>
                update({ ...x, from: ev.target.value as string })
              }
            >
              {displayOptions.map((o) => (
                <MenuItem key={o.key} value={o.key}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
            {isArchived && (
              <FormHelperText>
                {t('This actor has been archived. Please select another.')}
              </FormHelperText>
            )}
            {isMissing && (
              <FormHelperText>
                {t('This actor has been removed. Please select another.')}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
      renderTarget={(x, update) => {
        const { displayOptions, isArchived, isMissing } = describeRowActor(
          targetTpl.data.supportingActors,
          otherOptions,
          x.to
        );
        return (
          <FormControl variant="outlined" fullWidth error={isArchived || isMissing}>
            <InputLabel>{t('Actor in new experience')}</InputLabel>
            <Select
              fullWidth
              label={t('Actor in new experience')}
              value={x.to}
              onChange={(ev) => update({ ...x, to: ev.target.value as string })}
            >
              {displayOptions.map((o) => (
                <MenuItem key={o.key} value={o.key}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
            {isArchived && (
              <FormHelperText>
                {t('This actor has been archived. Please select another.')}
              </FormHelperText>
            )}
            {isMissing && (
              <FormHelperText>
                {t('This actor has been removed. Please select another.')}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export const ActionSettingsEnrollInExperience = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation
}: {
  value: IAutomationActionTemplateEnrollInExperience;
  onChange: (nextValue: IAutomationActionTemplateEnrollInExperience) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
}) => {
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const { t } = useUnsafeTranslation();
  const [tpls, loading, error] = useMappedLoadingValue(
    useExperienceTemplatesByOrgId(org.id),
    (ts) => {
      return sortBy(
        ts.filter((tpl) => {
          return (
            tpl.data.status !== ExperienceTemplateStatus.ARCHIVED &&
            tpl.data.status !== ExperienceTemplateStatus.ROUGH_DRAFT &&
            hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, ['edit'])
          );
        }),
        (x) => x.data.adminTitle || x.data.title
      );
    }
  );

  const selectedTemplate =
    tpls?.find((tpl) => tpl.id === value.tplToEnrollIn) || null;

  if (error) {
    return <SomethingWentWrong height={200} />;
  }

  if (loading || !tpls) {
    return <Loader height={200} />;
  }

  return (
    <FlexContainerVertical fullWidth spacing={3}>
      <AutocompleteSingle
        fullWidth
        value={selectedTemplate}
        options={tpls}
        getOptionLabel={(o) => o.data.adminTitle || o.data.title}
        getOptionSelected={(o, v) => o.id === v.id}
        placeholder={t('Select experience')}
        onChange={(nextValue) =>
          onChange({
            ...value,
            tplToEnrollIn: nextValue?.id || '',
            supportingActors: []
          })
        }
        filterOptions={(os, s) => {
          const t = toSearchTermRegexp(s.inputValue);
          return os.filter(
            (o) => t.exec(o.data.adminTitle) || t.exec(o.data.title)
          );
        }}
      />
      {selectedTemplate &&
        value.tplToEnrollIn === expTpl.templateId ? (
          <InfoBox color="blue">
            <strong>{t('Re-enroll in the current experience')}</strong>
            <p>
              {t(
                "The participant's current experience will be archived, and then they will be re-enrolled to start a new experience."
              )}
            </p>
          </InfoBox>
        ) : (
          <>
            {selectedTemplate &&
              !!getSelectableSupportingActors(
                selectedTemplate.data.supportingActors,
                { includeChannelActors: false }
              ).length && (
                <SupportingActorMappings
                  value={value.supportingActors}
                  onChange={(nextValue) =>
                    onChange({
                      ...value,
                      supportingActors: nextValue
                    })
                  }
                  targetTpl={selectedTemplate}
                  currentActors={expTpl.supportingActors}
                />
              )}
            <InfoBox color="blue">
              {t(`Participants will be enrolled instantly, as long as they meet the enrollment criteria set in the selected experience's Participants settings.
When these criteria are not met, an issue will be shown in this experience.`)}
            </InfoBox>
          </>
        )}
    </FlexContainerVertical>
  );
};

export const ActionSettingsEnrollInExperienceReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateEnrollInExperience;
  expTpl: TaskBuilderContext;
}) => {
  const currentUser = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const actorDescriptors = useActorDescriptorsFromTemplate(
    expTpl.supportingActors
  );

  const actor = actorDescriptors[value.actorToEnroll] || null;
  const [tpl, tplLoading] = useMappedLoadingValue(
    useExperienceTemplateById(
      value.tplToEnrollIn || '_____TPL_ID_WHICH_DOES_NOT_EXIST_____' // cannot call with empty id
    ),
    (tpl) => {
      if (
        tpl &&
        !hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, ['view'])
      ) {
        return null;
      }
      return tpl;
    }
  );

  // Descriptors alone can't distinguish "archived" from "deleted" (neither
  // carries the archived flag), so mapping rows resolve directly against the
  // raw supportingActors map instead.
  const describeMappingActor = (
    actors: TaskBuilderContext['supportingActors'],
    key: string
  ): string => {
    const actorRecord = getSupportingActorForDisplay(actors, key);
    if (!actorRecord) return t('[deleted actor]');
    return actorRecord.archived
      ? t('{{ name }} [archived actor]', { name: actorRecord.name })
      : actorRecord.name;
  };

  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who to enroll')}>
          {actor?.label || '...'}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Where to enroll')}>
          {value.tplToEnrollIn === expTpl.templateId
            ? t('Re-enroll in the current experience')
            : tpl
              ? getTplAdminTitle(tpl)
              : '...'}
        </DataFieldWithLabel>
      </FormRow>

      {!!value.supportingActors.length &&
        value.tplToEnrollIn !== expTpl.templateId && (
        <FormRow>
          <DataFieldWithLabel label={t('Supporting Actors')}>
            <FlexContainerVertical spacing={0}>
              {value.supportingActors.map((x, i) => {
                return (
                  <FlexContainer key={i} spacing={0.5} wrap="wrap">
                    <div>{describeMappingActor(expTpl.supportingActors, x.from)}</div>
                    <ArrowRight size={12} />
                    <div>
                      {tplLoading
                        ? '...'
                        : describeMappingActor(
                            tpl?.data.supportingActors || EMPTY_OBJ,
                            x.to
                          )}
                    </div>
                  </FlexContainer>
                );
              })}
            </FlexContainerVertical>
          </DataFieldWithLabel>
        </FormRow>
      )}
    </div>
  );
};
